/* eslint-disable no-use-before-define */

import { useBillingAddressForm } from './BillingAddressForm/use-billing-address-form';
import { useBookingDetailsForm } from './BookingDetailsForm/use-booking-details-form';
import { useIdentityVerificationForm } from './IdentityVerificationForm/use-identity-verification-form';
import { NEW_CARD_OPTION } from './PaymentForm/CreditCardSelectField';
import { usePaymentForm } from './PaymentForm/use-payment-form';
import { useSocialEventPolicyForm } from './SocialEventPolicyForm/use-social-event-policy-form';

export const useCheckoutForms = ({ intl, onChange, defaultValues }) => {
  const getAllFormValues = (newValues) => {
    return {
      bookingDetails: bookingDetailsForm.getValues(),
      payment: paymentForm.getValues(),
      billingAddress: billingAddressForm.getValues(),
      socialEventPolicyForm: socialEventPolicyForm.getValues(),
      identityVerification: identityVerificationForm.getValues(),
      ...(newValues || {})
    };
  };

  const { bookingDetailsForm } = useBookingDetailsForm({
    onChange: (newValues, changedFields) => {
      onChange?.(
        getAllFormValues({ bookingDetails: newValues }),
        changedFields
      );
    },
    defaultValues: defaultValues?.bookingDetails,
    intl
  });

  const { paymentForm } = usePaymentForm({
    onChange: (newValues, changedFields) => {
      onChange?.(getAllFormValues({ payment: newValues }), changedFields);
    },
    defaultValues: defaultValues?.payment,
    intl
  });

  const { socialEventPolicyForm } = useSocialEventPolicyForm(intl);

  const { billingAddressForm, billingCountries } = useBillingAddressForm({
    intl,
    defaultValues: defaultValues?.billingAddress,
    onChange: (newValues, changedField) => {
      onChange?.(getAllFormValues({ billingAddress: newValues }), changedField);
    }
  });

  const { identityVerificationForm } = useIdentityVerificationForm(intl);

  /**
   * Submits all forms sequentially, collecting their values into an object.
   * If any form submission fails (returns null), the process stops and returns early.
   */
  const submitForms = async (
    { socialEventPolicy, payment, billingAddress, identityVerification },
    defaultPaymentMethod
  ) => {
    let allFormsValid = true;

    const formValues = {
      bookingDetails: null,
      socialEventPolicy: null,
      payment: null,
      billingAddress: null,
      identityVerification: null
    };

    const submitForm = async (form, key) => {
      await form.handleSubmit(() => {
        // this callback is only invoked if the form is valid
        formValues[key] = form.getValues();
      })();
      if (!formValues[key]) {
        allFormsValid = false;
      }
    };

    await submitForm(bookingDetailsForm, 'bookingDetails');

    if (socialEventPolicy) {
      await submitForm(socialEventPolicyForm, 'socialEventPolicy');
    }

    if (payment) {
      await submitForm(paymentForm, 'payment');
    } else {
      let selectedCreditCard = paymentForm.getValues(
        'selectedCreditCard'
      );
      if (selectedCreditCard?.id === NEW_CARD_OPTION.id) {
        // this scenario can occur when applied credits cover the total,
        // but the user also selected "Add a new card".
        // In this case, set the payment method to the users default card,
        // since a payment method id is still required for the booking request
        selectedCreditCard = defaultPaymentMethod;
      }

      formValues.payment = {
        ...paymentForm.getValues(),
        selectedCreditCard
      };
    }

    if (billingAddress) {
      await submitForm(billingAddressForm, 'billingAddress');
    }

    if (identityVerification) {
      await submitForm(identityVerificationForm, 'identityVerification');
    }

    return allFormsValid && formValues;
  };

  return {
    bookingDetailsForm,
    socialEventPolicyForm,
    paymentForm,
    billingAddressForm,
    identityVerificationForm,
    billingCountries,
    submitForms
  };
};
